/**
 * Re-reading an open record after «Retry processing» (#1096).
 *
 * The retried run takes minutes, and nothing else re-reads the record: the
 * panel sat at `pending` until it was reopened. The detail query polls while
 * the run is in flight — queued or in a processing step — for as long as
 * waiting stays reasonable, and stops on its own at the first status the run
 * will not leave by itself.
 *
 * Each poll is a full GET /records/:id (several D1 reads), so the cadence is
 * gentle; the window covers a worst-case run — ASR and LLM with their step
 * retries — rather than a typical one (code review on #1264).
 */
import {
  isHaltedRecordStatus,
  isPendingRecordStatus,
  isProcessingRecordStatus,
} from '@/lib/records/status';

export const RETRY_POLL_INTERVAL_MS = 15_000;
export const RETRY_WATCH_MS = 30 * 60 * 1000;
/**
 * Right after the click the first read can still carry the old failure — a
 * refetch that failed, or a run that failed again before it — and a status
 * that never changes would never start the polling. The watch polls through
 * this much time regardless of the status.
 */
export const RETRY_GRACE_MS = 30_000;

export interface RetryWatch {
  /** Stop polling after this moment, whatever the status. */
  until: number;
  /** Poll until this moment even while the status still reads as settled. */
  graceUntil: number;
}

export function startRetryWatch(nowMs: number): RetryWatch {
  return { until: nowMs + RETRY_WATCH_MS, graceUntil: nowMs + RETRY_GRACE_MS };
}

/**
 * The retried run is still going: queued or in one of the processing steps.
 * A halted status — waiting for a start or for funds — is not: nothing moves
 * it without a person, so polling it would only load the server.
 */
export function isRetryRunInFlight(status: string | null | undefined): boolean {
  if (isHaltedRecordStatus(status)) return false;
  return isPendingRecordStatus(status) || isProcessingRecordStatus(status);
}

/** `refetchInterval` contribution of a retry; `watch` is null when none was started. */
export function retryRefetchInterval(
  status: string | null | undefined,
  watch: RetryWatch | null,
  nowMs: number
): number | false {
  if (watch === null || nowMs >= watch.until) return false;
  return isRetryRunInFlight(status) || nowMs < watch.graceUntil
    ? RETRY_POLL_INTERVAL_MS
    : false;
}

/**
 * `refetchInterval` contribution of a record opened mid-pipeline, with no
 * retry behind it (#1851). Nothing else re-reads it once its audio is
 * archived: the panel never learned the analysis had landed, so it kept the
 * transcript fetched before it — `speaker_N` labels the analysis's role map
 * turns into "Agent/Client" — and showed no analysis until reopened after
 * the cache went stale.
 *
 * Only the processing steps: a run moving on its own. A queued or waiting
 * record (`awaiting_chat_completion` can wait for hours) is not polled. Same
 * cadence and window as a retry, counted from the record's last change, so a
 * run stuck in a step stops being polled too.
 */
export function processingRefetchInterval(
  status: string | null | undefined,
  lastChangeAt: string | null | undefined,
  nowMs: number
): number | false {
  if (!isProcessingRecordStatus(status) || !lastChangeAt) return false;
  const since = Date.parse(lastChangeAt);
  return Number.isFinite(since) && nowMs - since < RETRY_WATCH_MS
    ? RETRY_POLL_INTERVAL_MS
    : false;
}
